import React from 'react';
import { Box, Button, CircularProgress, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';
import {
  Psychology as UnderstandProblemIcon,
  MenuBook as ExplainTopicIcon,
  Calculate as SolveIcon,
  FactCheck as CheckAnswerIcon,
  Image as ImageQuestionIcon,
  StopCircle as StopIcon,
} from '@mui/icons-material';
import type { CueOffer } from '../../hooks/useCueObserver';
import type { CueType } from '../../services/gnCueService';

const ICON_BY_TYPE: Record<CueType, React.ReactElement> = {
  SOLVE: <SolveIcon />,
  UNDERSTAND_PROBLEM: <UnderstandProblemIcon />,
  EXPLAIN_TOPIC: <ExplainTopicIcon />,
  CHECK_ANSWER: <CheckAnswerIcon />,
  IMAGE_QUESTION: <ImageQuestionIcon />,
};

interface CueChipProps {
  offer: CueOffer | null;
  generating: boolean;
  streamingText: string | null;
  onAccept: (type: CueType) => void;
  onStop: () => void;
  onDismiss: () => void;
}

/**
 * The unified ambient help-cue popover (spec 046 US2, FR-008/FR-011/FR-012). Absorbs spec
 * 044's PasteActionPopover — the same three teaching actions plus CHECK_ANSWER and
 * IMAGE_QUESTION are all offered through this one mechanism. Never steals focus: it's a
 * dismissible suggestion, not a modal blocking the writing area.
 */
const CueChip: React.FC<CueChipProps> = ({ offer, generating, streamingText, onAccept, onStop, onDismiss }) => {
  return (
    <Popover
      open={!!offer}
      onClose={generating ? undefined : onDismiss}
      anchorReference="anchorPosition"
      anchorPosition={offer?.position ?? { top: 0, left: 0 }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      disableAutoFocus
      disableEnforceFocus
    >
      <Box sx={{ p: 2, width: 320 }} data-testid="gn-cue-chip">
        {generating ? (
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={16} />
              <Typography variant="subtitle2">Working on it…</Typography>
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Stop">
                <IconButton size="small" onClick={onStop}>
                  <StopIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ maxHeight: 160, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
              {streamingText || '…'}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1}>
            {offer?.cues.map((cue) => (
              <Button
                key={cue.type}
                fullWidth
                variant="outlined"
                startIcon={ICON_BY_TYPE[cue.type]}
                onClick={() => onAccept(cue.type)}
                sx={{ justifyContent: 'flex-start' }}
                data-testid={`gn-cue-${cue.type}`}
              >
                {cue.label}
              </Button>
            ))}
          </Stack>
        )}
      </Box>
    </Popover>
  );
};

export default CueChip;
